
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Brain, AlertTriangle, TrendingDown, ArrowUpRight, Search, Zap, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

const RiskAnalysis = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleRunAnalysis = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">AI Risk Analysis</h1>
        <Brain className="h-8 w-8 text-primary" />
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>AI-Powered Risk Assessment</CardTitle>
          <CardDescription>
            Advanced risk analysis powered by machine learning algorithms
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This tool analyzes your loan portfolio and provides insights into potential risks, 
            early warning signals, and opportunities for portfolio optimization.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative grow">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search loan ID or borrower name..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button 
              variant="default" 
              className="flex gap-2 items-center"
              onClick={handleRunAnalysis}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  Run AI Analysis
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex justify-between items-center">
              Portfolio Risk Score
              <AlertTriangle className="h-4 w-4" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="text-4xl font-bold">68</div>
              <div className="text-sm text-muted-foreground">out of 100</div>
            </div>
            <Progress value={68} className="h-2 mt-2" />
            <p className="text-xs text-muted-foreground mt-2">
              Moderate risk level, consider rebalancing high-risk segments
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex justify-between items-center">
              Early Warning Signals
              <TrendingDown className="h-4 w-4" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="text-4xl font-bold">12</div>
              <div className="text-sm text-muted-foreground">loans flagged</div>
            </div>
            <Progress value={12} max={154} className="h-2 mt-2 bg-slate-200" />
            <p className="text-xs text-muted-foreground mt-2">
              7.8% of your portfolio shows early warning signals
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex justify-between items-center">
              Risk Trend
              <ArrowUpRight className="h-4 w-4" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="text-4xl font-bold text-amber-500">↑</div>
              <div className="text-sm text-muted-foreground">Increasing slightly</div>
            </div>
            <p className="text-xs text-muted-foreground mt-6">
              Risk profile increased by 2.3% over the last 30 days
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="insights">
        <TabsList>
          <TabsTrigger value="insights">Risk Insights</TabsTrigger>
          <TabsTrigger value="borrowers">Borrower Analysis</TabsTrigger>
          <TabsTrigger value="recommendations">AI Recommendations</TabsTrigger>
        </TabsList>
        
        <TabsContent value="insights" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Key Risk Insights</CardTitle>
              <CardDescription>AI-identified patterns in your loan portfolio</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium">Sector Concentration Risk</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        35% of your portfolio is concentrated in real estate, which is above the recommended threshold of 25%. 
                        This could increase vulnerability to sector-specific downturns.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="bg-amber-50">High Concentration</Badge>
                        <Badge variant="outline" className="bg-amber-50">Real Estate</Badge>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium">Payment Behavior Shift</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        7 borrowers have shown late payment patterns in the last 60 days despite having good credit scores.
                        This may indicate liquidity issues not reflected in traditional metrics.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="bg-red-50">Payment Delay</Badge>
                        <Badge variant="outline" className="bg-red-50">High Priority</Badge>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start gap-4">
                    <TrendingDown className="h-5 w-5 text-green-500 mt-0.5" />
                    <div>
                      <h3 className="font-medium">Improving Segment</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Small business loans in the technology sector are showing improving repayment patterns and 
                        lower-than-average default rates. Consider increasing allocation to this segment.
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="bg-green-50">Opportunity</Badge>
                        <Badge variant="outline" className="bg-green-50">Technology</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="borrowers" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Borrower Risk Analysis</CardTitle>
              <CardDescription>AI assessment of borrower behavior and risk factors</CardDescription>
            </CardHeader>
            <CardContent className="h-80 flex items-center justify-center">
              <div className="text-center">
                <Brain className="h-16 w-16 text-muted-foreground mx-auto" />
                <p className="mt-4 text-muted-foreground">
                  Detailed borrower risk analysis would appear here
                </p>
                <Button variant="outline" className="mt-4">View Flagged Borrowers</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="recommendations" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Recommendations</CardTitle>
              <CardDescription>Strategic suggestions based on risk analysis</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start gap-4">
                    <Zap className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-medium">Diversify Real Estate Exposure</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Consider reducing real estate exposure by 10% and redistributing to technology and healthcare 
                        sectors which show stronger risk-adjusted returns.
                      </p>
                      <div className="mt-2">
                        <Button variant="outline" size="sm">Apply Strategy</Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start gap-4">
                    <Zap className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-medium">Increase Collateral Requirements</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        For new loans in sectors showing increasing default trends, consider increasing collateral 
                        requirements by 15% to mitigate potential losses.
                      </p>
                      <div className="mt-2">
                        <Button variant="outline" size="sm">Apply Strategy</Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-start gap-4">
                    <Zap className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-medium">Early Intervention Program</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Implement proactive outreach to the 7 borrowers showing payment pattern changes. AI suggests 
                        this could prevent 85% of potential defaults.
                      </p>
                      <div className="mt-2">
                        <Button variant="outline" size="sm">Implement Program</Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RiskAnalysis;
